import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';

import * as api from '~/Services/Service';
import * as constants from '~/constant';
import Loading from '~/Components/Loading';

const DISSERTATIVA_MAX = 500;

// NPS 0..10 não tem tipo nativo — vem como ESCOLHA com 11 opções "0".."10".
// (ver TechSpec §1 / seed). Detectamos para renderizar como botões 0–10.
const isNps = pergunta => {
  if (pergunta.tipo !== constants.TIPOS_PESQUISA.ESCOLHA) return false;
  const opcoes = pergunta.opcoes ? pergunta.opcoes : [];
  if (opcoes.length !== 11) return false;
  return opcoes.every(o => /^\d+$/.test(String(o.descricao).trim()));
};

// Pergunta-pai dispara a condicional (3.1) quando seu valor casa com condicao_valor_pai.
// condicao_valor_pai = codigo_resposta da opção que dispara (ex.: opção "Sim").
// Confirmar o formato no JSON real do backend antes de integrar.
const valorPaiCasa = (valorPai, condicao) => {
  if (valorPai == null || valorPai === '') return false;
  return String(valorPai) === String(condicao);
};

const perguntaVisivel = (pergunta, values) => {
  if (!pergunta.codigo_pergunta_pai) return true;
  const valorPai = values[String(pergunta.codigo_pergunta_pai)];
  return valorPaiCasa(valorPai, pergunta.condicao_valor_pai);
};

const temValor = valor => valor !== '' && valor != null;

// Schema Yup dinâmico: obrigatórias sempre exigidas; condicional (3.1) exigida só
// quando visível (a pergunta-pai casa com a condição).
const construirSchema = perguntas => {
  const shape = {};
  perguntas.forEach(pergunta => {
    const nome = String(pergunta.codigo);
    if (pergunta.codigo_pergunta_pai) {
      const pai = String(pergunta.codigo_pergunta_pai);
      shape[nome] = Yup.mixed().when(pai, {
        is: valorPai => valorPaiCasa(valorPai, pergunta.condicao_valor_pai),
        then: Yup.mixed().test(
          'cond-obrigatoria',
          'Resposta obrigatória',
          temValor
        ),
        otherwise: Yup.mixed().notRequired(),
      });
    } else if (pergunta.obrigatoria) {
      shape[nome] = Yup.mixed().test(
        'obrigatoria',
        'Resposta obrigatória',
        temValor
      );
    }
  });
  return Yup.object().shape(shape);
};

export default function FormularioPesquisa({
  tipo,
  pessoa,
  onSalvo,
  onCancelar,
}) {
  const [perguntas, setPerguntas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [indiceAtual, setIndiceAtual] = useState(0);

  useEffect(() => {
    let ativo = true;
    const carregar = async () => {
      setIsLoading(true);
      try {
        const resultado = await api.obterPerguntasPesquisaEvento(tipo);
        const lista = resultado.perguntas ? resultado.perguntas : [];
        const ordenadas = [...lista].sort((a, b) => a.ordem - b.ordem);
        if (ativo) setPerguntas(ordenadas);
      } catch (err) {
        const mensagem =
          err && err.message ? err.message : 'Erro ao carregar o formulário.';
        toast.error(mensagem);
      } finally {
        if (ativo) setIsLoading(false);
      }
    };
    carregar();
    return () => {
      ativo = false;
    };
  }, [tipo]);

  const initialValues = perguntas.reduce((acc, pergunta) => {
    acc[String(pergunta.codigo)] = '';
    return acc;
  }, {});

  const montarItens = values =>
    perguntas
      .filter(pergunta => perguntaVisivel(pergunta, values))
      .map(pergunta => {
        const valor = values[String(pergunta.codigo)];
        if (!temValor(valor)) return null;
        if (pergunta.tipo === constants.TIPOS_PESQUISA.DISSERTATIVA) {
          return { codigo_pergunta: pergunta.codigo, dissertativa: valor };
        }
        return { codigo_pergunta: pergunta.codigo, codigo_resposta: valor };
      })
      .filter(item => item != null);

  const salvar = async (values, actions) => {
    try {
      const payload = {
        tipo,
        origem: pessoa.origem,
        codigo: pessoa.codigo,
        itens: montarItens(values),
      };
      if (pessoa.codigo_boleto != null) {
        payload.codigo_boleto = pessoa.codigo_boleto;
      }
      if (pessoa.codigo_prevenda != null) {
        payload.codigo_prevenda = pessoa.codigo_prevenda;
      }

      await api.responderPesquisaEvento(payload);
      toast.success('Pesquisa registrada com sucesso.');
      onSalvo();
    } catch (err) {
      const mensagem =
        err && err.message ? err.message : 'Erro ao salvar a pesquisa.';
      toast.error(mensagem);
    } finally {
      actions.setSubmitting(false);
    }
  };

  if (isLoading) return <Loading />;

  if (perguntas.length === 0) {
    return (
      <p className="text-muted mb-0">
        Nenhuma pergunta cadastrada para esta pesquisa.
      </p>
    );
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={construirSchema(perguntas)}
      onSubmit={salvar}
    >
      {({ values, errors, touched, setFieldValue, isSubmitting }) => {
        // Recalcula a cada render — quando a pergunta-pai muda, a condicional
        // entra/sai da sequência automaticamente.
        const perguntasVisiveis = perguntas.filter(p =>
          perguntaVisivel(p, values)
        );
        const total = perguntasVisiveis.length;
        const indice = Math.min(indiceAtual, Math.max(0, total - 1));
        const perguntaAtual = perguntasVisiveis[indice];
        const ehPrimeira = indice === 0;
        const ehUltima = indice >= total - 1;

        if (!perguntaAtual) {
          return (
            <Form>
              <p className="text-muted mb-0">
                Nenhuma pergunta para exibir.
              </p>
            </Form>
          );
        }

        const nome = String(perguntaAtual.codigo);
        const valorAtual = values[nome];
        const erro = touched[nome] && errors[nome];
        const requerResposta =
          !!perguntaAtual.obrigatoria || !!perguntaAtual.codigo_pergunta_pai;
        const podeAvancar = !requerResposta || temValor(valorAtual);

        // Em ESCOLHA/NPS, tocar na opção já avança (exceto na última, que aguarda
        // o Salvar). setTimeout dá um frame para o setFieldValue propagar e o
        // usuário ver o destaque do botão antes da transição.
        const escolherEAvancar = codigoResposta => {
          setFieldValue(nome, codigoResposta);
          if (!ehUltima) {
            setTimeout(() => setIndiceAtual(i => i + 1), 150);
          }
        };

        return (
          <Form>
            <div className="text-muted small mb-2">
              Pergunta {indice + 1} de {total}
            </div>

            <div className="mb-4">
              <label className="d-block mb-3">
                {perguntaAtual.descricao}
                {requerResposta && <span className="text-danger"> *</span>}
              </label>

              {isNps(perguntaAtual) && (
                // NPS 0–10: 11 botões dividindo a linha igualmente (flex-fill) e
                // sem quebra, para caber tudo numa linha só no modal lg.
                <div className="d-flex flex-nowrap">
                  {[...perguntaAtual.opcoes]
                    .sort((a, b) => a.ordem - b.ordem)
                    .map((opcao, idx) => (
                      <Button
                        key={opcao.codigo}
                        type="button"
                        size="sm"
                        className={`flex-fill px-0${idx > 0 ? ' ml-1' : ''}`}
                        variant={
                          valorAtual === opcao.codigo
                            ? 'primary'
                            : 'outline-secondary'
                        }
                        onClick={() => escolherEAvancar(opcao.codigo)}
                      >
                        {opcao.descricao}
                      </Button>
                    ))}
                </div>
              )}

              {!isNps(perguntaAtual) &&
                perguntaAtual.tipo === constants.TIPOS_PESQUISA.ESCOLHA && (
                  <div className="d-flex flex-column">
                    {[...perguntaAtual.opcoes]
                      .sort((a, b) => a.ordem - b.ordem)
                      .map(opcao => (
                        <Button
                          key={opcao.codigo}
                          type="button"
                          className="text-left mb-2"
                          variant={
                            valorAtual === opcao.codigo
                              ? 'primary'
                              : 'outline-secondary'
                          }
                          onClick={() => escolherEAvancar(opcao.codigo)}
                        >
                          {opcao.descricao}
                        </Button>
                      ))}
                  </div>
                )}

              {perguntaAtual.tipo === constants.TIPOS_PESQUISA.DISSERTATIVA && (
                <>
                  <textarea
                    className="form-control"
                    rows="4"
                    maxLength={DISSERTATIVA_MAX}
                    value={valorAtual ? valorAtual : ''}
                    onChange={e => setFieldValue(nome, e.target.value)}
                  />
                  <small className="text-muted">
                    {(valorAtual ? valorAtual.length : 0)}/{DISSERTATIVA_MAX}
                  </small>
                </>
              )}

              {erro && <div className="text-danger small mt-1">{erro}</div>}
            </div>

            <div className="d-flex justify-content-between">
              <Button
                type="button"
                variant="outline-secondary"
                onClick={onCancelar}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <div>
                {!ehPrimeira && (
                  <Button
                    type="button"
                    variant="outline-secondary"
                    className="mr-2"
                    onClick={() => setIndiceAtual(i => Math.max(0, i - 1))}
                    disabled={isSubmitting}
                  >
                    Voltar
                  </Button>
                )}
                {!ehUltima && (
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => setIndiceAtual(i => i + 1)}
                    disabled={isSubmitting || !podeAvancar}
                  >
                    Próxima
                  </Button>
                )}
                {ehUltima && (
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isSubmitting || !podeAvancar}
                  >
                    {isSubmitting ? 'Salvando...' : 'Salvar pesquisa'}
                  </Button>
                )}
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}

FormularioPesquisa.propTypes = {
  tipo: PropTypes.oneOf(['E', 'S']).isRequired,
  pessoa: PropTypes.object.isRequired,
  onSalvo: PropTypes.func.isRequired,
  onCancelar: PropTypes.func.isRequired,
};
